import type {
  AnySupportedLanguage,
  FranceSupportedLanguage,
  ItalySupportedLanguage,
  GermanySupportedLanguage,
  LangProps,
} from './types'

/**
 * Object containing the French program language options for the CountryField component.
 */
export const frlanguageOptions = {
  en: [
    { value: 'en', label: 'English' },
    { value: 'fr', label: 'French' },
  ],
  fr: [
    { value: 'en', label: 'Anglais' },
    { value: 'fr', label: 'Français' },
  ],
}

/**
 * Object containing the Italian program language options for the CountryField component.
 */
export const itlanguageOptions = {
  en: [
    { value: 'en', label: 'English' },
    { value: 'it', label: 'Italian' },
  ],
  it: [
    { value: 'en', label: 'Inglese' },
    { value: 'it', label: 'Italiano' },
  ],
}

/**
 * Object containing the German program language options for the CountryField component.
 */
export const delanguageOptions = {
  en: [
    { value: 'en', label: 'English' },
    { value: 'de', label: 'German' },
  ],
  de: [
    { value: 'en', label: 'Englisch' },
    { value: 'de', label: 'Deutsch' },
  ],
}

/**
 * Checks if the given language option is a French supported language.
 * @param lang The language option to check.
 * @returns True if the language option is a French supported language, false otherwise.
 */
function isFrenchLanguageOption(lang: AnySupportedLanguage): lang is FranceSupportedLanguage {
  return Object.keys(frlanguageOptions).includes(lang)
}

/**
 * Checks if the given language option is a Italian supported language.
 * @param lang The language option to check.
 * @returns True if the language option is a Italian supported language, false otherwise.
 */
function isItalianLanguageOption(lang: AnySupportedLanguage): lang is ItalySupportedLanguage {
  return Object.keys(itlanguageOptions).includes(lang)
}

/**
 * Checks if the given language option is a German supported language.
 * @param lang The language option to check.
 * @returns True if the language option is a German supported language, false otherwise.
 */
function isGermanLanguageOption(lang: AnySupportedLanguage): lang is GermanySupportedLanguage {
  return Object.keys(delanguageOptions).includes(lang)
}

/**
 * Retrieves the language options based on the program code and language.
 * @param program - The program code.
 * @param lang - The language.
 * @returns The language options.
 */
export function getLanguageOptions(program: string, lang: AnySupportedLanguage) {
  if (program === 'de' || program === 'at') {
    const isGerman = isGermanLanguageOption(lang)
    return isGerman ? delanguageOptions[lang] : delanguageOptions.de
  }
  if (program === 'fr') {
    const isFrench = isFrenchLanguageOption(lang)
    return isFrench ? frlanguageOptions[lang] : frlanguageOptions.fr
  }
  const isItalian = isItalianLanguageOption(lang)
  return isItalian ? itlanguageOptions[lang] : itlanguageOptions.it
}

/**
 * Checks if the given object is a language object.
 * @param obj The object to check.
 * @returns True if the object is a language object, false otherwise.
 */
export function isLanguageObject(obj: unknown): obj is LangProps {
  return typeof obj === 'object' && obj !== null && 'document_title_home' in obj
}
